import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { fmtDate } from "../lib/constants";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function RecycleBin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/leads/recycle-bin").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const restore = async (l) => {
    try { await api.post(`/leads/${l.id}/restore`); toast.success(`${l.first_name} ${l.last_name} restored`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const purge = async (l) => {
    if (!window.confirm(`Permanently delete ${l.first_name} ${l.last_name} (${l.uid}) and all quotes, activities, messages and documents? This cannot be undone.`)) return;
    try { await api.delete(`/leads/${l.id}/permanent`); toast.success("Permanently deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/app/leads")} className="text-sm text-muted-foreground flex items-center gap-1"><Icons.ChevronLeft size={16} /> Back to leads</button>
      <div>
        <h1 className="text-3xl font-head font-extrabold tracking-tight flex items-center gap-3"><Icons.Trash2 size={28} className="text-muted-foreground" /> Recycle Bin</h1>
        <p className="text-muted-foreground">Deleted leads are kept for 30 days, then removed permanently. Restore or purge them here.</p>
      </div>
      <div className="bg-card border border-border rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="recycle-table">
          <thead><tr className="text-left label-caps border-b border-border">
            <th className="p-3">Lead</th><th>Contact</th><th>Deleted</th><th>By</th><th>Purges in</th><th className="text-right pr-3">Actions</th></tr></thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} className="border-b border-border/50" data-testid={`recycle-row-${l.uid}`}>
                <td className="p-3"><div className="font-semibold">{l.first_name} {l.last_name}</div><div className="text-xs text-primary">{l.uid}</div></td>
                <td className="text-xs text-muted-foreground">{[l.phone, l.email].filter(Boolean).join(" · ")}</td>
                <td className="whitespace-nowrap">{fmtDate(l.deleted_at)}</td>
                <td>{l.deleted_by || "—"}</td>
                <td><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${l.days_left <= 5 ? "bg-red-100 text-red-700" : "bg-muted"}`}>{l.days_left ?? "—"} days</span></td>
                <td className="text-right pr-3 whitespace-nowrap">
                  <button onClick={() => restore(l)} data-testid={`restore-${l.uid}`} className="px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 text-xs font-semibold hover:bg-emerald-50 mr-2 inline-flex items-center gap-1"><Icons.RotateCcw size={13} /> Restore</button>
                  <button onClick={() => purge(l)} data-testid={`purge-${l.uid}`} className="px-3 py-1.5 rounded-md border border-red-300 text-red-600 text-xs font-semibold hover:bg-red-50 inline-flex items-center gap-1"><Icons.Trash2 size={13} /> Delete forever</button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">Recycle bin is empty.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
